import { useMemo, useEffect, useContext } from 'react'
import * as React from 'react'
import type { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewTile from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Divider from 'LuxKit/Divider'
import FlightCartReviewTileExtension from 'components/Pages/CartReviewPage/FlightCartReviewTileExtension'
import { FlightViewTypes } from 'constants/flight'
import styled from 'styled-components'
import useRefreshMultiCartFlightItem from '../hooks/useRefreshMultiCartFlightItem'
import Group from 'components/utils/Group'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import { buildStandaloneFlightSearchUrl, getFlightLoyaltyProductType, getFlightLoyaltyProductTypeRank, getFlightsTotal } from 'lib/flights/flightUtils'
import ExpiryTimer from 'components/Pages/CartReviewPage/ExpiryTimer'
import { rem } from 'polished'
import GeoContext from 'contexts/geoContext'
import { arrayToMap, sortBy } from 'lib/array/arrayUtils'
import { isTwoOneWaysItem } from 'components/Pages/CartReviewPage/CartReviewUtils'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import { useSelector } from '@xstate/react'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { dateIsBefore, startOfDay } from 'lib/datetime/dateUtils'
import { getLocalDate } from 'lib/datetime/calendarUtils'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import UndoDeletedItemsTile from '../UndoDeletedItemsTile'
import MultiCartParticipantLabel from './MultiCartParticipantLabel'
import getLoyaltyEarnKeyFromFlightItems from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKeyFromFlightItems'
import SharedCartComments from '../Comments/SharedCartComments'
import { FormattedMessage, useIntl } from 'react-intl'
import MaldivesBundleDiscountBadge from 'components/Common/Labels/MaldivesBundleDiscountBadge'
import { useMaldivesBundleRefreshContext } from '../hooks/MaldivesBundleRefreshContext'
import useCharterRewardAvailability from 'hooks/Flights/useCharterRewardAvailability'
import RewardBundlePriceDisplay from 'components/OfferPage/FlashHotel/RewardBundlePriceDisplay'
import Caption from 'LuxKit/Typography/Caption'
import { getFlightReviewDateSummary, getFlightReviewOccupancy, getFlightReviewTitle } from './flightReviewTileUtils'

const ExtensionContainer = styled(Group)`
  grid-area: extension;
`

const DividerPadding = styled.div`
  padding-inline: ${rem(20)};
`

interface Props {
  multiCartItem: App.MultiCartFlightItem
  tileVariant?: 'default' | 'condensed'
  hideActionButtons?: boolean
  hidePrices?: boolean
  hidePassengerCount?: boolean
  hideLoyaltyPoints?: boolean
}

function FlightMultiCartReviewTile({ multiCartItem, tileVariant = 'default', hideActionButtons = false, hidePrices = false, hidePassengerCount = false, hideLoyaltyPoints = false }: Props) {
  const intl = useIntl()
  const item = multiCartItem.item as App.Checkout.FlightItem

  // Check if this flight is eligible for bundle pricing and being auto-refreshed
  const { refreshingFlightIds, eligibleFlightIds } = useMaldivesBundleRefreshContext()
  const isBundleEligible = eligibleFlightIds.has(item.itemId)
  const isBundleRefreshing = refreshingFlightIds.has(item.itemId)

  // Pass bundle eligibility to refresh hook so expired flights refresh with correct pricing context
  const { dataStatus, initiateRefresh } = useRefreshMultiCartFlightItem(multiCartItem, { isBundleEligible })

  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const multiCartActorRef = MultiCartContext.useActorRef()
  const cartItemsState = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type === 'shared'
  const showComments = !hideActionButtons && isSharedCart

  // Maldives bundle discount: flight is using bundle pricing (no 15% markup)
  const isBundlePriced = multiCartItem.isBundlePriced ?? false

  const activeFlightCartItems = cartItemsState.status === 'loaded' && activeCartId ? Object.values(cartItemsState.data[activeCartId] || {})
    .filter(multiCartItem => isFlightItem(multiCartItem.item) && multiCartItem.state === 'active') : []

  const departingDate = item.flights[0].departingDate
  const isPast = !!departingDate && dateIsBefore(getLocalDate(departingDate), startOfDay(new Date()))

  const failedRefresh = dataStatus === 'refreshFailed'
  const hasExpired = dataStatus === 'expired'
  const isAvailable = !failedRefresh && !hasExpired && !isPast
  const canShowExtensionAndTimer = !!multiCartItem.expiry && dataStatus !== 'refreshFailed'
  const isSavedForLater = multiCartItem.state === 'save_for_later'

  const flightSearchUrl = useMemo(() => buildStandaloneFlightSearchUrl({
    originAirportCode: multiCartItem.originAirportCode,
    originAirportName: multiCartItem.flights[0].departingAirportName,
    destinationAirportCode: multiCartItem.destinationAirportCode,
    destinationAirportName: multiCartItem.flights[0].arrivalAirportName,
    departDate: multiCartItem.flights[0].departingDate,
    returnDate: multiCartItem.flights.length > 1 ? multiCartItem.flights[1].departingDate : null,
    adults: multiCartItem.occupants.adults,
    children: multiCartItem.occupants.children ?? 0,
    infants: multiCartItem.occupants.infants ?? 0,
    childrenAge: multiCartItem.occupants.childrenAge?.join(',') ?? '',
    fareClass: multiCartItem.flights[0].fareFamily?.cabin?.toLowerCase() ?? 'economy',
    fareType: multiCartItem.fareType,
  }), [
    multiCartItem.originAirportCode,
    multiCartItem.flights,
    multiCartItem.destinationAirportCode,
    multiCartItem.occupants,
    multiCartItem.fareType,
  ])

  const { airports } = useContext(GeoContext)

  const hasTwoOneWaysFlightsInUserCart = () => {
    if (!activeFlightCartItems.length) return false

    const twoOneWaysFlights = activeFlightCartItems.find(item => (item.item as App.Checkout.FlightItem).viewType === FlightViewTypes.TWO_ONE_WAYS)
    if (!twoOneWaysFlights) return false

    return isTwoOneWaysItem(twoOneWaysFlights.item as App.Checkout.FlightItem)
  }

  const isTwoOneWaysAndUserCartContainsFlights = () => {
    return activeFlightCartItems.length && isTwoOneWaysItem(item)
  }

  useEffect(() => {
    if (!isAvailable && multiCartItem.state === 'active') {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [multiCartItem.id],
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [isAvailable, multiCartItem.state, activeCartId, multiCartItem.id, multiCartReviewActorRef])

  const formattedOccupancy = useMemo(() => {
    return getFlightReviewOccupancy(intl, multiCartItem.occupants, hidePassengerCount)
  }, [multiCartItem.occupants, hidePassengerCount, intl])

  const title = useMemo(() => {
    return getFlightReviewTitle(intl, multiCartItem.viewType)
  }, [multiCartItem.viewType, intl])

  const dates = useMemo(() => {
    return getFlightReviewDateSummary(intl, multiCartItem.flights, multiCartItem.viewType)
  }, [multiCartItem.flights, multiCartItem.viewType, intl])

  const totalCost = useMemo(() => getFlightsTotal(item), [item])

  const isReward = !!item.isClassicReward
  const rewardPassengerCount = useMemo(
    () => item.passengers.filter(p => p.type !== 'infant').length,
    [item.passengers],
  )
  const rewardLegs = useMemo(
    () => item.flights.map(f => ({
      journeyKey: f.rewardJourneyKey ?? f.journeyKey,
      legTotal: f.cost ?? 0,
    })),
    [item.flights],
  )
  const {
    available: rewardAvailable,
    isLoading: isRewardLoading,
    pointsPerPerson: rewardPointsPerPerson,
    cashCoPayPerPerson: rewardCashCoPayPerPerson,
  } = useCharterRewardAvailability({
    legs: rewardLegs,
    passengerCount: rewardPassengerCount,
    disabled: !isReward || rewardLegs.length === 0,
  })
  const rewardTotalPoints = (rewardPointsPerPerson ?? 0) * rewardPassengerCount
  const rewardTotalCash = (rewardCashCoPayPerPerson ?? 0) * rewardPassengerCount
  // Reward flight with 0 inventory — treat as unavailable at cart level.
  const rewardUnavailable = isReward && !rewardAvailable && !isRewardLoading
  const isBookable = multiCartItem.state !== 'deleted' && isAvailable && !rewardUnavailable

  const bannerLabelVariant = () => {
    if (isPast) return 'pastDates'
    if (hasExpired) return 'expired'
    if (failedRefresh) return 'failedRefresh'
    if (rewardUnavailable) return 'rewardUnavailable'
    if (isBookable && multiCartItem.state === 'save_for_later' && hasTwoOneWaysFlightsInUserCart()) return 'bookingLimit'
    if (multiCartItem.state === 'save_for_later' && isTwoOneWaysAndUserCartContainsFlights()) return 'bookingLimit'
    return null
  }

  const bannerContent = () => {
    switch (bannerLabelVariant()) {
      case 'pastDates':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'XUmx0kwrYLkLaqaM4XCG',
              defaultMessage: 'Your selected dates are in the past.',
              description: 'Warning message when flight dates in cart are past dates',
            })}
            </BodyText>
            <TextLink weight="bold" to="/flights" target="_blank">
              {intl.formatMessage({
                id: 'r2nHQHRc0vJ01JsHbomu',
                defaultMessage: 'Check availability',
                description: 'Hyperlink text to check availability in the shopping cart',
              })}
            </TextLink>
          </Group>
        )
      case 'expired':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'ZDZaT5fohIrcwwKdI0gJ',
              defaultMessage: 'This flight price has expired',
              description: 'Warning notification message when flight price has expired',
            })}
            </BodyText>
            <TextLink weight="bold" onClick={initiateRefresh}>
              {intl.formatMessage({
                id: '7Sxljrd5uHOal_uyr_f1',
                defaultMessage: 'Refresh search',
                description: 'Link text to refresh flight search in the flight search or booking flow',
              })}
            </TextLink>
          </Group>
        )
      case 'failedRefresh':
        return (
          <Group direction="horizontal" gap={12}>
            <BodyText variant="medium">{intl.formatMessage({
              id: 'RenayifZewRDwd0ZNIjC',
              defaultMessage: 'We couldn’t refresh your flight.',
              description: 'Error message when flight price refresh fails in cart',
            })}
            </BodyText>
            <TextLink weight="bold" to={flightSearchUrl} target="_blank">
              {intl.formatMessage({
                id: '_6pF-iGMJyiSSZNkN_WP',
                defaultMessage: 'Search for flights',
                description: 'Link text to search for flights in the flight search or booking flow',
              })}
            </TextLink>
          </Group>
        )
      case 'rewardUnavailable':
        return intl.formatMessage({
          id: 'r3YxULsdPtwfyotnVn2c',
          defaultMessage: 'Reward seats are no longer available for this flight. Please delete and select a different fare.',
          description: 'Warning banner on the cart flight tile when Classic Rewards availability returns no inventory for the selected reward flight.',
        })
      case 'bookingLimit':
        return intl.formatMessage({
          id: 'Osk_Hc7c5G3GZx14hgkX',
          defaultMessage: 'This flight needs to be booked separately from your current selection',
          description: 'Warning message when flight needs to be booked separately from current selection',
        })
      default:
        return ''
    }
  }

  const banner = () => {
    if (!bannerLabelVariant()) return null

    return (
      <MessageBanner kind="warning" density="compact" icon={<LineInfoCircleIcon />}>
        {bannerContent()}
      </MessageBanner>
    )
  }

  const handleSaveForLater = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // TODO: How  to test for hasTwoOneWaysFlightsInUserCart() || isTwoOneWaysItem(item)
    // this tile is already saved for later, so user is +adding them back, so bring back all other flights which are heading in same direction.
    if (isSavedForLater) {
      const flightCartItemIdsToSaveForLater = (isSavedForLater && (hasTwoOneWaysFlightsInUserCart() || isTwoOneWaysItem(item))) ?
          activeFlightCartItems
            .map(flightCartItem => flightCartItem.id)
        : []
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [...flightCartItemIdsToSaveForLater, multiCartItem.id],
        saveForLater: !(multiCartItem.state === 'save_for_later'),
      })
    } else {
      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId!,
        itemIds: [multiCartItem.id],
        saveForLater: !(multiCartItem.state === 'save_for_later'),
      })
    }
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if (!activeCartId) return

    multiCartActorRef.send({
      type: 'delete items from given cart',
      cartId: activeCartId,
      itemIds: [multiCartItem.id],
      currentStates: {
        [multiCartItem.id]: multiCartItem.state,
      },
    })
  }

  const extension = useMemo(() => {
    if (!canShowExtensionAndTimer) return null

    return (
      <ExtensionContainer direction="vertical" gap={8}>
        <DividerPadding><Divider kind="primary" /></DividerPadding>
        {multiCartItem.flights.map((flight, index) => (
          <React.Fragment key={flight.journeyId}>
            <FlightCartReviewTileExtension flight={flight} isClassicReward={isReward} />
            {index < multiCartItem.flights.length - 1 && <DividerPadding><Divider kind="primary" /></DividerPadding>}
          </React.Fragment>
        ))}
      </ExtensionContainer>
    )
  }, [canShowExtensionAndTimer, multiCartItem.flights, isReward])

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    // Classic Rewards flight items aren't earnable — points cover the flight and the
    // cash co-pay is excluded. Return empty calculation requests so no earn line renders
    // on the cart-review flight tile.
    if (isReward) {
      return { calculationRequests: [], calculationType: 'exact' }
    }
    const airportsByCode = arrayToMap(airports, airport => airport.code)

    const loyaltyProductTypes = multiCartItem.flights.map(flight => {
      const departingAirport = airportsByCode.get(flight.departingAirportCode)
      const arrivalAirport = airportsByCode.get(flight.arrivalAirportCode)

      const isDomestic = departingAirport?.countryCode === arrivalAirport?.countryCode
      return getFlightLoyaltyProductType(flight.fareFamily?.cabin?.toLowerCase() ?? 'economy', isDomestic)
    })

    const rankedLoyaltyProductTypes = sortBy(loyaltyProductTypes, productType => getFlightLoyaltyProductTypeRank(productType), 'asc')
    const highestRankedProductType = rankedLoyaltyProductTypes[0]

    const earnKey = getLoyaltyEarnKeyFromFlightItems(item.flights, highestRankedProductType, airports)

    return {
      calculationRequests: [{
        loyaltyProductType: highestRankedProductType,
        price: totalCost,
        earnKey,
        offerId: '',
        packageId: '',
      }],
      calculationType: 'exact',
    }
  }, [airports, multiCartItem.flights, totalCost, item.flights, isReward])

  if (dataStatus === 'refreshing' || isBundleRefreshing) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (multiCartItem.state === 'deleted') {
    return <UndoDeletedItemsTile
      title={title}
      onUndoClick={() => {
        multiCartActorRef.send({
          itemIds: [multiCartItem.id],
          type: 'undo delete',
        })
      }}
    />
  }

  if (tileVariant === 'condensed') {
    const condensedPrice = isReward && rewardAvailable ? <RewardBundlePriceDisplay
      size="S"
      points={rewardTotalPoints}
      cash={rewardTotalCash}
    /> : <CartReviewPriceStack
      price={totalCost}
      value={totalCost}
    />

    return (
      <CondensedCartReviewTile
        banner={banner()}
        content={<CartReviewTileContent
          title={title}
          dates={dates}
          occupancy={formattedOccupancy}
          loyaltyPointsEarningConfiguration={hideLoyaltyPoints ? undefined : loyaltyPointsEarningConfiguration}
          label={<ExpiryTimer
            canShowExtension={canShowExtensionAndTimer}
            expiry={multiCartItem.expiry ?? 0}
          />}
        />}
        onDelete={hideActionButtons ? undefined : handleDelete}
        price={condensedPrice}
      />
    )
  }

  const actions = !hideActionButtons ? <CartReviewTileActions
    isBookable={isBookable}
    saveForLater={isSavedForLater}
    onSaveForLater={handleSaveForLater}
    onDelete={handleDelete}
  /> : undefined

  const price = !hidePrices ? (isReward && rewardAvailable ? <Group direction="vertical" horizontalAlign="end" gap={2}>
    <RewardBundlePriceDisplay
      size="S"
      points={rewardTotalPoints}
      cash={rewardTotalCash}
    />
    <Caption variant="medium" colour="neutral-three">
      <FormattedMessage
        id="4KMnOJnbkwyq1BVr29ae"
        defaultMessage="Reward flight"
        description="Label below the points + cash price in the cart review tile indicating this is a Classic Rewards flight"
      />
    </Caption>
  </Group> : <CartReviewPriceStack
    price={totalCost}
    value={totalCost}
  />) : null

  const commentsFooter = showComments && activeCartId ? (
    <SharedCartComments
      multiCartId={activeCartId}
      itemIds={[multiCartItem.id]}
      noBorder
    />
  ) : undefined

  return (
    <CartReviewTile
      content={<CartReviewTileContent
        title={title}
        dates={dates}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        label={<>
          <ExpiryTimer
            canShowExtension={canShowExtensionAndTimer}
            expiry={multiCartItem.expiry ?? 0}
          />
          <MultiCartParticipantLabel item={multiCartItem} />
          {isBundlePriced && <MaldivesBundleDiscountBadge />}
        </>}
      />}
      price={price}
      banner={banner()}
      extension={extension}
      footer={commentsFooter}
      isBookable={isBookable}
      saveForLater={isSavedForLater}
      actions={actions}
    />
  )
}

export default FlightMultiCartReviewTile
